/**
 * Responsible for using the details behavior within a floating label.
 * @module elements/fields/iconifyDetails
 */
import modal from '../../modules/modal';
import setup, { VISIBLE_CLASS_NAME } from './iconify-setup';

export { VISIBLE_CLASS_NAME };

/**
 * Contains the list of icons associated with a more details iconify floating
 * label.
 * @type {Object}
 */
export const iconList = [
  { name: 'details', iconId: 'details', svg: (text) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><title>${text}</title><<path d="M8 0a8 8 0 1 0 8 8 8 8 0 0 0-8-8zm0 15.25A7.25 7.25 0 1 1 15.25 8 7.258 7.258 0 0 1 8 15.25zm-.064-4.797a.797.797 0 0 0-.579.23.763.763 0 0 0-.236.568.743.743 0 0 0 .244.592.843.843 0 0 0 .571.212.814.814 0 0 0 .557-.214.748.748 0 0 0 .241-.589.787.787 0 0 0-.798-.798zm2.42-5.442a2.227 2.227 0 0 0-.919-.78 3.125 3.125 0 0 0-1.371-.286 3 3 0 0 0-1.462.343 2.515 2.515 0 0 0-.954.865 1.937 1.937 0 0 0-.327 1.031.647.647 0 0 0 .206.458.68.68 0 0 0 .506.212q.5 0 .7-.605a2.37 2.37 0 0 1 .471-.876 1.138 1.138 0 0 1 .868-.297 1.165 1.165 0 0 1 .822.295.947.947 0 0 1 .319.723.821.821 0 0 1-.104.407 1.534 1.534 0 0 1-.257.34q-.153.153-.495.453a8.484 8.484 0 0 0-.622.592 1.899 1.899 0 0 0-.509 1.358.715.715 0 0 0 .19.541.652.652 0 0 0 .469.182.596.596 0 0 0 .637-.557q.06-.263.088-.367a1.105 1.105 0 0 1 .083-.209 1.185 1.185 0 0 1 .163-.23 3.712 3.712 0 0 1 .292-.292q.66-.6.913-.838a2.446 2.446 0 0 0 .439-.592 1.655 1.655 0 0 0 .185-.798 1.913 1.913 0 0 0-.324-1.071z"/></svg>` },
];

/**
 * Defines configurable options
 * @type {Object}
 */
const DEFAULT_MORE_DETAILS = {
  modal,
};

/**
 * Responsible for showing a more details icons within a floating element. Once
 * the icon has been interacted with, it will show a modal. This is different
 * than an iconify-behaviors as this work doesn't need to hook into the
 * floating label events cycle, but rather utilizes the same setup, therefore
 * allowing us to reuse things such as setting up icons and the applicable
 * elements used in a floating label.
 *
 * To configure this work, make sure the following data attributes have been
 * provided:
 *
 * - data-iconify-more-details: contains the title for the icon used for i18n
 *   and accessibility
 * - data-open: an ID to a modal
 *
 * Since this work will leverage the modal, any modal related data attributes
 * can be applied here. Here's an example in HTML to wire this dude up:
 *
 * @example
 * <div
 *     class="floating-label"
 *     data-iconify-more-details="More Details"
 *     data-open="modal-example">
 *   <label for="the-dude" class="floating-label__label">The Dude</label>
 *   <input id="the-dude" class="floating-label__input">
 * </div>
 *
 * <div id="modal-example" class="modal u-center" data-type="default-modal">
 *   <p>modal content goes here</p>
 * </div>
 *
 * @param {Object} options - Configurable options
 * @returns {Function} Initialization of iconify more details
 */
const iconifyMoreDetails = (options = {}) => (...nodes) => {
  const opts = Object.assign({}, DEFAULT_MORE_DETAILS, options);
  const { modal: modalFunc } = opts;
  const { container, iconNodes } = setup(iconList, nodes, opts);
  if (!iconNodes) {
    return;
  }

  const { details: detailsIcon } = iconNodes;
  detailsIcon.classList.add(VISIBLE_CLASS_NAME);
  detailsIcon.addEventListener('click', () => {
    modalFunc(container, {
      triggerOpen: true,
    });
  });
};

export default iconifyMoreDetails;




// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/iconify-details.js